import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Box, Divider, TextField, Typography, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { routerPaths } from "src/app/routerPaths";
import SocialAuth from "src/auth/components/SocialAuth/SocialAuth";
import { useSnackbar } from "src/theme/SnackbarProvider/SnackbarProvider";
import RegisterWithEmailForm from "src/auth/pages/Register/components/RegisterWithEmailForm/RegisterWithEmailForm";
import { FirebaseError, getUserFriendlyFirebaseErrorMessage } from "src/error/FirebaseError/firebaseError";
import FirebaseEmailAuthService from "src/auth/services/FirebaseAuthenticationService/emailAuth/FirebaseEmailAuthentication.service";
import { getUserFriendlyErrorMessage, RestAPIError } from "src/error/restAPIError/RestAPIError";
import UserPreferencesStateService from "src/userPreferences/UserPreferencesStateService";
import FirebaseSocialAuthenticationService from "src/auth/services/FirebaseAuthenticationService/socialAuth/FirebaseSocialAuthentication.service";
import { useAuthPageContext } from "src/auth/components/AuthLayout/AuthPageContext";
import RequestInvitationCode from "src/auth/components/requestInvitationCode/RequestInvitationCode";
import { InvitationType } from "src/auth/services/invitationsService/invitations.types";
import { FirebaseErrorCodes } from "src/error/FirebaseError/firebaseError.constants";
import { INVITATIONS_PARAM_NAME } from "src/auth/auth.types";
import { getApplicationRegistrationCode, getSocialAuthDisabled, getRegistrationCodeDisabled } from "src/envService";
import { outlinedNoBorderSx } from "src/auth/pages/authInputStyles";
import { enqueueErrorSnackbarWithReference } from "src/theme/SnackbarProvider/enqueueErrorSnackbarWithReference";

const uniqueId = "ab02918f-d559-47ba-9662-ea6b3a3606d0";

export const DATA_TEST_ID = {
  REGISTRATION_CODE_INPUT: `register-registration-code-input-${uniqueId}`,
  REGISTER_CONTAINER: `register-container-${uniqueId}`,
  FORM: `register-form-${uniqueId}`,
  USERNAME_INPUT: `register-username-input-${uniqueId}`,
  EMAIL_INPUT: `register-email-input-${uniqueId}`,
  PASSWORD_INPUT: `register-password-input-${uniqueId}`,
  REGISTER_BUTTON: `register-button-${uniqueId}`,
  REGISTER_BUTTON_CIRCULAR_PROGRESS: `register-button-circular-progress-${uniqueId}`,
  FORGOT_PASSWORD_LINK: `register-forgot-password-link-${uniqueId}`,
  REGISTER_USING: `register-using-${uniqueId}`,
  FIREBASE_AUTH_CONTAINER: `firebase-auth-container-${uniqueId}`,
  LOGIN_LINK: `register-login-link-${uniqueId}`,
  LANGUAGE_SELECTOR: `register-language-selector-${uniqueId}`,
};

const Register: React.FC = () => {
  const [registrationCode, setRegistrationCode] = useState<string>("");
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageLoading } = useAuthPageContext();

  const registrationCodeDisabled = useMemo(() => {
    return getRegistrationCodeDisabled().toLowerCase() === "true";
  }, []);

  // Check for invitation code in URL params when component mounts
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteCodeParam = params.get(INVITATIONS_PARAM_NAME);

    if (inviteCodeParam && !registrationCodeDisabled) {
      setRegistrationCode(inviteCodeParam);
      // Remove the invite code from the URL
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete(INVITATIONS_PARAM_NAME);
      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true }
      );
    }
  }, [location, navigate, registrationCodeDisabled]);

  // a state to determine if the user is currently registering with email
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setPageLoading(isLoading, t("auth.pages.register.registeringYou"));
  }, [isLoading, setPageLoading, t]);

  const handleError = useCallback(
    async (error: Error) => {
      if (error instanceof RestAPIError) {
        console.error(error);
        enqueueSnackbar(getUserFriendlyErrorMessage(error), { variant: "error" });
      } else if (error instanceof FirebaseError) {
        if (error.errorCode === FirebaseErrorCodes.INVALID_REGISTRATION_CODE) {
          console.error(error);
        } else {
          console.warn(error);
        }
        enqueueSnackbar(getUserFriendlyFirebaseErrorMessage(error), { variant: "error" });
      } else {
        console.error(error);
        enqueueErrorSnackbarWithReference(t("auth.errors.registerFailedGeneric"), { where: "Register", error });
      }
    },
    [enqueueSnackbar, t]
  );

  const applicationRegistrationCode = useMemo(() => {
    return getApplicationRegistrationCode();
  }, []);

  const socialAuthDisabled = useMemo(() => {
    return getSocialAuthDisabled().toLowerCase() === "true";
  }, []);

  /* -----------
   * callbacks to pass to the child components
   */
  const handleRegistrationCodeChanged = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRegistrationCode(event.target.value);
  };

  /**
   * Handles what happens after social registration (same process as login)
   * @param user
   */
  const handlePostLogin = useCallback(async () => {
    try {
      setIsLoading(true);
      const prefs = UserPreferencesStateService.getInstance().getUserPreferences();
      if (!prefs?.accepted_tc || isNaN(prefs?.accepted_tc.getTime())) {
        navigate(routerPaths.CONSENT, { replace: true });
      } else {
        navigate(routerPaths.ROOT, { replace: true });
        enqueueSnackbar(t("auth.pages.register.welcomeBack"), { variant: "success" });
      }
    } catch (error) {
      const firebaseSocialAuthServiceInstance = FirebaseSocialAuthenticationService.getInstance();
      await firebaseSocialAuthServiceInstance.logout(); // this does not throw an error, at least in the current implementation
      console.info("Social registration failed. Logging out user.");

      await handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, enqueueSnackbar, handleError, t]);

  /* ------------
   * Actual registration handlers
   */
  /**
   * Handle the register form submission
   * @param email
   * @param password
   */
  const handleRegister = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const firebaseEmailAuthServiceInstance = FirebaseEmailAuthService.getInstance();
        // if the instance has application registration code set, we should use that instead of the one entered by the user.
        const registrationCodeToUse = registrationCode || applicationRegistrationCode;
        // We're using the mail as the username for now, since we don't have any use case in the app for it
        await firebaseEmailAuthServiceInstance.register(
          email,
          password,
          email,
          !registrationCodeDisabled ? registrationCodeToUse : undefined
        );
        enqueueSnackbar(t("auth.verificationEmailSentShort"), { variant: "success" });
        // IMPORTANT NOTE: after the preferences are added, or fail to be added, we should log the user out immediately,
        // since if we don't do that, the user may be able to access the application without verifying their email
        // or accepting the dpa.
        await firebaseEmailAuthServiceInstance.logout();
        console.info("Registration requires email verification. Logging out user.");

        // navigate to the verify email page
        navigate(routerPaths.VERIFY_EMAIL, { replace: true });
      } catch (e) {
        await handleError(e as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      navigate,
      enqueueSnackbar,
      setIsLoading,
      registrationCode,
      handleError,
      applicationRegistrationCode,
      registrationCodeDisabled,
      t,
    ]
  );

  /**
   * A callback function for the social auth component to set the loading state
   */
  const notifyOnSocialLoading = useCallback((socialAuthLoading: boolean) => {
    setIsLoading(socialAuthLoading);
  }, []);

  return (
    <Box data-testid={DATA_TEST_ID.REGISTER_CONTAINER} sx={{ minHeight: "100%" }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent={"space-evenly"}
        gap={theme.fixedSpacing(theme.tabiyaSpacing.lg)}
        width={"100%"}
        sx={{
          color: theme.palette.common.white,
          "& .MuiTypography-root": { color: theme.palette.common.white },
          "& .MuiLink-root": { color: theme.palette.common.white },
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={theme.fixedSpacing(1.5)}>
          <Typography variant="h3" alignSelf="flex-start" textAlign="center">
            {t("auth.pages.register.signUpTitle")}
          </Typography>
          <Typography variant="body2" alignSelf="flex-start" component="div">
            <Trans
              i18nKey="auth.pages.register.signUpDescription"
              components={[
                <strong key="sign-up-desc-0" />,
                <strong key="sign-up-desc-1" />,
                <strong key="sign-up-desc-2" />,
              ]}
            />
          </Typography>
        </Box>
        {!applicationRegistrationCode && !registrationCodeDisabled && (
          <React.Fragment>
            <Typography variant="subtitle2">{t("auth.pages.register.enterRegistrationCode")}</Typography>
            <TextField
              fullWidth
              label={t("auth.pages.register.registrationCode")}
              variant="outlined"
              required
              value={registrationCode}
              onChange={(e) => handleRegistrationCodeChanged(e)}
              sx={outlinedNoBorderSx}
              inputProps={{ "data-testid": DATA_TEST_ID.REGISTRATION_CODE_INPUT }}
            />
          </React.Fragment>
        )}
        {!applicationRegistrationCode && !registrationCodeDisabled && (
          <Divider textAlign="center" style={{ width: "100%" }}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.common.white }}
              padding={theme.fixedSpacing(theme.tabiyaSpacing.sm)}
            >
              {t("auth.pages.register.andEitherContinueWith")}
            </Typography>
          </Divider>
        )}
        <RegisterWithEmailForm
          disabled={!registrationCode && !applicationRegistrationCode && !registrationCodeDisabled}
          notifyOnRegister={handleRegister}
          isRegistering={isLoading}
        />
        {!socialAuthDisabled && (
          <SocialAuth
            postLoginHandler={handlePostLogin}
            isLoading={isLoading}
            disabled={!registrationCode && !applicationRegistrationCode && !registrationCodeDisabled}
            label={t("auth.pages.register.registerWithGoogle")}
            notifyOnLoading={notifyOnSocialLoading}
            registrationCode={registrationCode || applicationRegistrationCode}
          />
        )}
        {!applicationRegistrationCode && !registrationCodeDisabled && (
          <RequestInvitationCode invitationCodeType={InvitationType.REGISTER} />
        )}
      </Box>
    </Box>
  );
};

export default Register;
